import React, { useState } from 'react';
import { useSession } from '../../context/SessionContext';

// Centralized FAQ items
const ALL_FAQ_ITEMS = {
  // Welcome Screen FAQs
  welcome: [
    {
      id: 'welcome-1',
      question: "What should I expect from the questionnaire?",
      answer: "The questionnaire is an unscored Q&A designed to understand your experience and skills better. These questions are not practical challenges to solve."
    },
    {
      id: 'welcome-2',
      question: "What should I expect from the challenge?",
      answer: "The challenge consists of real-world tasks similar to those you'll encounter in your role. You'll showcase your practical abilities through hands-on exercises."
    },
    {
      id: 'welcome-5',
      question: "How do you ensure fairness and prevent bias?",
      answer: "Our assessment processes are carefully designed to focus strictly on hard skills and standardized evaluation criteria. We regularly analyze our results to identify and eliminate any potential biases."
    }
  ],

  // Abandoned Screen FAQs
  abandoned: [
    {
      id: 'abandoned-1',
      question: 'Why did my evaluation expire?',
      answer: 'Evaluations expire once the allocated time has passed to maintain the integrity of the assessment process.'
    },
    {
      id: 'abandoned-2',
      question: 'Can I restart my evaluation?',
      answer: 'Unfortunately, evaluations cannot be restarted after expiration or abandonment. If you experienced technical difficulties, please contact support@tendent.ai.'
    },
    {
      id: 'abandoned-3',
      question: 'How can I contact the hiring team?',
      answer: 'You can contact the hiring team via the email address provided in your invitation or by replying directly to the email containing your evaluation link.'
    }
  ],

  // Upload Resume FAQs
  resume: [
    {
      id: 'resume-1',
      question: "What information should my resume include?",
      answer: "Your resume should detail your work experience, education, skills, certifications, and relevant projects. Highlight experiences directly related to the role, emphasizing specific achievements and measurable results whenever possible."
    },
    {
      id: 'resume-2',
      question: "What happens after I submit my resume?",
      answer: "Once your resume is submitted, it will become part of your evaluation profile. You'll then proceed to subsequent evaluation steps, including answering role-specific questions and completing a practical challenge."
    }
  ],

  // Challenge FAQs
  challenge: [
    {
      id: 'challenge-1',
      question: "What kind of questions will the challenge include?",
      answer: "The challenge consists of practical questions tailored to your specific role, designed to evaluate your hard skills and practical abilities. For example, if you are a software engineer, the challenge will contain coding tests, among other items."
    },
    {
      id: 'challenge-2',
      question: "Can I save and resume my progress later?",
      answer: "Yes, you can pause and resume your progress later - except in timed evaluations. If a timer is active, you'll need to complete and submit your responses before it runs out. Otherwise, you're free to continue the challenge at your own pace."
    },
    {
      id: 'challenge-3',
      question: "What happens if I don't finish before time expires?",
      answer: "If the timer expires, your current answers will be saved automatically, and you will be prompted to submit your incomplete responses. No further changes will be possible."
    },
    {
      id: 'challenge-4',
      question: "How is the challenge evaluated?",
      answer: "Your responses will be automatically scored by our system based on accuracy, completeness, and correctness. This helps us effectively assess your hard skills and expertise."
    }
  ],

  // Questionnaire FAQs
  questionnaire: [
    {
      id: 'questionnaire-1',
      question: "What types of questions will the questionnaire include?",
      answer: "The questionnaire includes various question formats such as multiple-choice, single-answer, and open-ended questions to understand your knowledge and professional experience better."
    },
    {
      id: 'questionnaire-2',
      question: "Can I save and resume the questionnaire later?",
      answer: "No, you must complete the questionnaire in one sitting. Once started, the timer counts down continuously, and responses must be submitted before time expires."
    },
    {
      id: 'questionnaire-3',
      question: "What happens if I don't complete the questionnaire in time?",
      answer: "Your current responses will automatically save when time expires, and you'll be prompted to submit your answers. You won't be able to make further edits."
    },
    {
      id: 'questionnaire-4',
      question: "How will the questionnaire be used?",
      answer: "The questionnaire is not scored but is intended to gather insights into your professional experience and expertise."
    }
  ]
};

function FAQ({ faqItems, faqType, title = "Frequently Asked Questions" }) {
  // Get countdown_enabled from session context
  const { sessionData } = useSession();
  const countdown_enabled = sessionData?.countdown_enabled ?? true; // Default to true if not available
  
  // State to track which FAQ items are expanded
  const [expandedFaqs, setExpandedFaqs] = useState({});
  
  // Toggle FAQ expansion
  const toggleFaq = (index) => {
    setExpandedFaqs(prev => ({
      ...prev,
      [index]: !prev[index]
    }));
  };

  // Determine which FAQ items to display
  let itemsToDisplay = faqType && ALL_FAQ_ITEMS[faqType] 
    ? ALL_FAQ_ITEMS[faqType] 
    : faqItems || [];

  // Filter out countdown-related questions when countdown_enabled is false
  if (!countdown_enabled) {
    const questionsToHide = [
      'questionnaire-2', // "Can I save and resume the questionnaire later?"
      'questionnaire-3', // "What happens if I don't complete the questionnaire in time?"
      'challenge-2',     // "Can I save and resume my progress later?"
      'challenge-3'      // "What happens if I don't finish before time expires?"
    ];
    
    itemsToDisplay = itemsToDisplay.filter(item => !questionsToHide.includes(item.id));
  }

  return (
    <div className="w-full p-6">
      <h3 className="text-2xl font-normal text-center mb-6">{title}</h3>
      
      <div className="space-y-4">
        {itemsToDisplay.map((item, index) => (
          <div key={item.id} className={index < itemsToDisplay.length - 1 ? "border-b border-gray-200 pb-4" : "pb-2"}>
            <button 
              onClick={() => toggleFaq(item.id)} 
              className="flex justify-between items-center w-full text-left font-medium text-gray-800 hover:text-blue-500 focus:outline-none transition-colors duration-200"
            >
              <span className="text-lg font-normal">{item.question}</span>
              <svg 
                className={`w-5 h-5 text-gray-500 transition-transform duration-200 ${expandedFaqs[item.id] ? 'transform rotate-180' : ''}`} 
                fill="none" 
                stroke="currentColor" 
                viewBox="0 0 24 24" 
                xmlns="http://www.w3.org/2000/svg"
              >
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M19 9l-7 7-7-7"></path>
              </svg>
            </button>
            <div 
              className={`mt-2 text-gray-600 overflow-hidden transition-all duration-200 ${
                expandedFaqs[item.id] ? 'max-h-96 opacity-100' : 'max-h-0 opacity-0'
              }`}
            >
              {item.answer}
            </div>
          </div>
        ))}
      </div>
    </div>
  );
}

export default FAQ;
